import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getMetrics, MetricRow } from '../api/analytics';
import PageHeader from '../components/shared/PageHeader';
import { SkelRow } from '../components/shared/Skeleton';
import { ChartTooltip, chartAxisProps } from '../components/shared/chartTheme';
import { fmtNumber, fmtCompact, fmtCurrency } from '../utils/format';

// ---- date helpers: <input type="date"> works with "YYYY-MM-DD" strings ----
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const todayIso = () => isoDay(new Date());
const daysAgoIso = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};
const yesterdayIso = () => daysAgoIso(1);

const RANGE_PRESETS = [
  { key: 'yesterday', label: 'yesterday' },
  { key: '7d',        label: '7d' },
  { key: '30d',       label: '30d' },
  { key: '90d',       label: '90d' }
] as const;
type RangeKey = (typeof RANGE_PRESETS)[number]['key'];

// Map a preset to its concrete date range.
const rangeFor = (key: RangeKey): { from: string; to: string } => {
  switch (key) {
    case 'yesterday': return { from: yesterdayIso(), to: yesterdayIso() };
    case '7d':        return { from: daysAgoIso(7),  to: todayIso() };
    case '30d':       return { from: daysAgoIso(30), to: todayIso() };
    case '90d':       return { from: daysAgoIso(90), to: todayIso() };
  }
};

// Stable colour per monetization type (shared by the donut, its legend and the table).
const MON_COLORS: Record<string, string> = {
  PPC: 'var(--success)',
  CPL: 'var(--warning)',
  REVSHARE: '#A855F7',
  ORGANIC: '#0EA5E9',
  UNKNOWN: 'var(--fg-3)'
};
const monColor = (t: string) => MON_COLORS[t] ?? 'var(--accent)';

const MIN_VIEWS = [
  { value: 0,   label: 'all'  },
  { value: 50,  label: '≥50'  },
  { value: 100, label: '≥100' },
  { value: 500, label: '≥500' }
] as const;

const CHART_METRICS = [
  { value: 'views',   label: 'Views'   },
  { value: 'clicks',  label: 'Clicks'  },
  { value: 'ctr',     label: 'CTR'     },
  { value: 'revenue', label: 'Revenue' },
  { value: 'rpm',     label: 'RPM'     }
] as const;
type ChartMetric = (typeof CHART_METRICS)[number]['value'];
type ChartDim = 'username' | 'pageKey';
const CHART_TOP = 10;

// Backend already aggregates per (pageKey, username), so we work with MetricRow
// directly. We render at most this many rows to keep the DOM light; KPI totals
// and the chart are still computed over the FULL filtered set.
const DISPLAY_LIMIT = 500;

type SortField = 'pageKey' | 'username' | 'views' | 'clicks' | 'ctr' | 'cpc' | 'revenue' | 'rpm';
type SortDir = 'asc' | 'desc';

// Map key: the \u0000 separator never appears in a pageKey/username, so the pair is unambiguous.
const rowKey = (pageKey: string, username: string) => `${pageKey}\u0000${username}`;

const pct = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';

// Small labelled wrapper so every filter/control says what it is (+ a hover hint).
const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }} title={hint}>
    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-3)' }}>{label}</span>
    {children}
  </label>
);

const AnalyticsDashboardPage: React.FC = () => {
  const [from, setFrom] = useState<string>(yesterdayIso());
  const [to, setTo] = useState<string>(yesterdayIso());
  const [preset, setPreset] = useState<RangeKey | ''>('yesterday');
  const [query, setQuery] = useState('');
  const [monetization, setMonetization] = useState<string>('PPC');
  const [minViews, setMinViews] = useState<number>(0);
  const [sortField, setSortField] = useState<SortField>('views');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [chartMetric, setChartMetric] = useState<ChartMetric>('views');
  const [chartDim, setChartDim] = useState<ChartDim>('username');

  const validRange = !!from && !!to && from <= to;

  // Server call: only refetches when the date range changes (in the queryKey).
  // Everything else (search/monetization/minViews/sort) is applied client-side.
  const { data, isLoading, isError } = useQuery<MetricRow[]>({
    queryKey: ['analytics-metrics', from, to],
    queryFn: () => getMetrics(from, to),
    enabled: validRange
  });

  // Rows come pre-aggregated from the backend (one per page×model, summed over
  // the range with CTR/revenue/RPM already computed). No client aggregation.
  const aggregated = useMemo<MetricRow[]>(() => data ?? [], [data]);

  // Base filter = search + minViews (NOT monetization). The donut uses this so it
  // always shows the full type split, even when a monetization type is selected.
  const baseFiltered = useMemo<MetricRow[]>(() => {
    const q = query.trim().toLowerCase();
    return aggregated.filter(a =>
      (!q || a.pageKey.toLowerCase().includes(q) || a.username.toLowerCase().includes(q)) &&
      a.views >= minViews
    );
  }, [aggregated, query, minViews]);

  // Table/KPI set = base + monetization filter + sort.
  const filteredSorted = useMemo<MetricRow[]>(() => {
    const filtered = monetization
      ? baseFiltered.filter(a => a.monetizationType === monetization)
      : baseFiltered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((x, y) => {
      const xv = x[sortField];
      const yv = y[sortField];
      if (typeof xv === 'string' && typeof yv === 'string') return xv.localeCompare(yv) * dir;
      return ((xv as number) - (yv as number)) * dir;
    });
  }, [baseFiltered, monetization, sortField, sortDir]);

  // KPI strip totals — computed over the currently visible rows.
  const summary = useMemo(() => {
    const models = new Set<string>();
    let views = 0, clicks = 0, revenue = 0;
    for (const r of filteredSorted) {
      views += r.views;
      clicks += r.clicks;
      revenue += r.revenue;
      models.add(r.username);
    }
    return {
      rows: filteredSorted.length,
      models: models.size,
      views,
      clicks,
      revenue,
      ctr: views > 0 ? (clicks / views) * 100 : null,
      rpm: views > 0 ? (revenue / views) * 1000 : null
    };
  }, [filteredSorted]);

  // Adjustable chart: re-aggregate the visible rows by the chosen dimension,
  // then take the top N by the chosen metric. Ratio metrics (CTR/RPM) are
  // recomputed from the summed totals — never averaged.
  const chartData = useMemo(() => {
    const m = new Map<string, { name: string; views: number; clicks: number; revenue: number }>();
    for (const r of filteredSorted) {
      const name = chartDim === 'username' ? r.username : r.pageKey;
      const e = m.get(name) ?? { name, views: 0, clicks: 0, revenue: 0 };
      e.views += r.views;
      e.clicks += r.clicks;
      e.revenue += r.revenue;
      m.set(name, e);
    }
    const value = (e: { views: number; clicks: number; revenue: number }) => {
      switch (chartMetric) {
        case 'views':   return e.views;
        case 'clicks':  return e.clicks;
        case 'revenue': return e.revenue;
        case 'rpm':     return e.views > 0 ? (e.revenue / e.views) * 1000 : 0;
        case 'ctr':     return e.views > 0 ? (e.clicks / e.views) * 100 : 0;
      }
    };
    return Array.from(m.values())
      .map(e => ({ name: e.name, value: value(e) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, CHART_TOP);
  }, [filteredSorted, chartMetric, chartDim]);

  const isMoneyMetric = chartMetric === 'revenue' || chartMetric === 'rpm';
  const isPctMetric = chartMetric === 'ctr';
  const chartValueFmt = (v: number) =>
    isMoneyMetric ? fmtCurrency(v) : isPctMetric ? `${v.toFixed(2)}%` : fmtNumber(v);
  const chartTickFmt = (v: number) =>
    isMoneyMetric ? fmtCurrency(v, { whole: true }) : isPctMetric ? `${v}%` : fmtCompact(v);
  const chartMetricLabel = CHART_METRICS.find(m => m.value === chartMetric)?.label;

  // Donut: how many DISTINCT models fall under each monetization type.
  // Uses baseFiltered (ignores the monetization filter) so the split stays whole.
  const monetizationSplit = useMemo(() => {
    const byType = new Map<string, Set<string>>();
    for (const r of baseFiltered) {
      const t = r.monetizationType || 'UNKNOWN';
      if (!byType.has(t)) byType.set(t, new Set());
      byType.get(t)!.add(r.username);
    }
    return Array.from(byType.entries())
      .map(([type, models]) => ({ type, models: models.size }))
      .sort((a, b) => b.models - a.models);
  }, [baseFiltered]);
  const monTotalModels = monetizationSplit.reduce((s, x) => s + x.models, 0);

  // Monetization filter options come from the data (all + each type present).
  const monetizationOptions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of aggregated) set.add(r.monetizationType || 'UNKNOWN');
    const hasPpc = set.delete('PPC');                 // pull PPC out so we can pin it first
    const rest = Array.from(set).sort();
    return ['', ...(hasPpc ? ['PPC'] : []), ...rest]; // all · PPC · (the rest, alphabetical)
  }, [aggregated]);

  function toggleSort(f: SortField) {
    if (sortField === f) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('desc'); }
  }
  const ind = (f: SortField) => (sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const applyPreset = (key: RangeKey) => {
    const r = rangeFor(key);
    setFrom(r.from);
    setTo(r.to);
    setPreset(key);
  };

  const exportCsv = () => {
    const header = ['page_key', 'username', 'views', 'clicks', 'ctr_pct', 'monetization', 'cpc', 'revenue', 'rpm'];
    const lines = [header.join(',')];
    for (const r of filteredSorted) {
      lines.push([
        '"' + r.pageKey + '"',
        '"' + r.username + '"',
        r.views,
        r.clicks,
        r.ctr.toFixed(2),
        r.monetizationType,
        r.cpc.toFixed(2),
        r.revenue.toFixed(2),
        r.rpm.toFixed(2)
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const actions = (
    <button className="hf-btn" onClick={exportCsv} disabled={filteredSorted.length === 0}>
      <Download size={14} /> Export CSV
    </button>
  );

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="GA4 page × model metrics · views, CTR, revenue, RPM"
        actions={actions}
      />

      <div className="hf-content">
        {/* Filters */}
        <div className="hf-toolbar" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 14 }}>
          <Field label="range">
            <div className="hf-segmented">
              {RANGE_PRESETS.map(p => (
                <button key={p.key} className={preset === p.key ? 'on' : ''} onClick={() => applyPreset(p.key)}>{p.label}</button>
              ))}
            </div>
          </Field>
          <Field label="from">
            <div className="hf-input" style={{ width: 150 }}>
              <input type="date" value={from} max={to} onChange={e => { setFrom(e.target.value); setPreset(''); }} />
            </div>
          </Field>
          <Field label="to">
            <div className="hf-input" style={{ width: 150 }}>
              <input type="date" value={to} min={from} max={todayIso()} onChange={e => { setTo(e.target.value); setPreset(''); }} />
            </div>
          </Field>
          <Field label="monetization" hint="Filter by payout model (from model_profiles)">
            <div className="hf-segmented">
              {monetizationOptions.map(opt => (
                <button key={opt || 'all'} className={monetization === opt ? 'on' : ''} onClick={() => setMonetization(opt)}>{opt || 'all'}</button>
              ))}
            </div>
          </Field>
          <Field label="min views" hint="Hide rows below this view count — drops statistically noisy long-tail rows">
            <div className="hf-segmented">
              {MIN_VIEWS.map(m => (
                <button key={m.value} className={minViews === m.value ? 'on' : ''} onClick={() => setMinViews(m.value)}>{m.label}</button>
              ))}
            </div>
          </Field>
          <div className="hf-input" style={{ minWidth: 200, marginLeft: 'auto', alignSelf: 'flex-end' }}>
            <Search size={14} />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="filter by page or model" />
          </div>
        </div>

        {/* KPI strip */}
        <div className="hf-kpi-strip">
          <div className="hf-kpi accent">
            <span className="hf-kpi-label">Rows</span>
            <span className="hf-kpi-value">{fmtNumber(summary.rows)}</span>
            <span className="hf-kpi-sub">{fmtNumber(summary.models)} models</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Views</span>
            <span className="hf-kpi-value">{fmtNumber(summary.views)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Clicks</span>
            <span className="hf-kpi-value">{fmtNumber(summary.clicks)}</span>
            <span className="hf-kpi-sub">CTR {pct(summary.ctr)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Revenue</span>
            <span className="hf-kpi-value">{fmtCurrency(summary.revenue)}</span>
            <span className="hf-kpi-sub">PPC only</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">RPM</span>
            <span className="hf-kpi-value">{fmtCurrency(summary.rpm)}</span>
            <span className="hf-kpi-sub">revenue / 1k views</span>
          </div>
        </div>

        {/* Charts: adjustable top-N bar (left) + monetization donut (right) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)', gap: 16 }}>
          <div className="hf-card">
          <div className="hf-card-header" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>
              Top {CHART_TOP} {chartDim === 'username' ? 'models' : 'pages'} by {chartMetricLabel}
            </h3>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Field label="metric">
                <div className="hf-segmented">
                  {CHART_METRICS.map(m => (
                    <button key={m.value} className={chartMetric === m.value ? 'on' : ''} onClick={() => setChartMetric(m.value)}>{m.label}</button>
                  ))}
                </div>
              </Field>
              <Field label="group by">
                <div className="hf-segmented">
                  <button className={chartDim === 'username' ? 'on' : ''} onClick={() => setChartDim('username')}>model</button>
                  <button className={chartDim === 'pageKey' ? 'on' : ''} onClick={() => setChartDim('pageKey')}>page</button>
                </div>
              </Field>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 30)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 28, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 4" stroke="var(--border)" horizontal={false} />
              <XAxis type="number" {...chartAxisProps} tickFormatter={chartTickFmt} />
              <YAxis
                type="category"
                dataKey="name"
                width={170}
                interval={0}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'var(--fg-2)', fontSize: 12, fontFamily: 'var(--font-sans)' }}
                tickFormatter={(n: string) => (n.length > 24 ? n.slice(0, 23) + '…' : n)}
              />
              <Tooltip cursor={{ fill: 'var(--border)', opacity: 0.25 }} content={<ChartTooltip formatter={(v) => chartValueFmt(Number(v))} />} />
              <Bar dataKey="value" name={chartMetricLabel} fill="var(--accent)" radius={[0, 4, 4, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
          </div>

          <div className="hf-card">
            <div className="hf-card-header"><h3 style={{ margin: 0 }}>Models by monetization</h3></div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={monetizationSplit} dataKey="models" nameKey="type" cx="50%" cy="50%"
                     innerRadius={60} outerRadius={95} paddingAngle={2} stroke="var(--surface)">
                  {monetizationSplit.map(s => <Cell key={s.type} fill={monColor(s.type)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={(v) => `${fmtNumber(Number(v))} models`} />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', padding: '4px 8px 10px', justifyContent: 'center' }}>
              {monetizationSplit.map(s => (
                <span key={s.type} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fg-2)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 2, background: monColor(s.type) }} />
                  {s.type}
                  <span className="hf-mono" style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{fmtNumber(s.models)}</span>
                  <span style={{ color: 'var(--fg-3)' }}>
                    {monTotalModels > 0 ? `${((s.models / monTotalModels) * 100).toFixed(0)}%` : ''}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>

        {!validRange && <div className="hf-banner danger">"From" date must be on or before "to" date.</div>}
        {isError && <div className="hf-banner danger">Failed to load analytics metrics.</div>}

        {isLoading ? (
          <div className="hf-card flush">
            <table className="hf-table">
              <thead>
                <tr>
                  <th>page</th><th>model</th>
                  <th className="num">views</th><th className="num">clicks</th><th className="num">CTR</th>
                  <th>type</th>
                  <th className="num">CPC</th><th className="num">revenue</th><th className="num">RPM</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 10 }).map((_, i) => (
                  <SkelRow key={i} cols={9} numCols={[2, 3, 4, 6, 7, 8]} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="hf-card flush">
            <table className="hf-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('pageKey')} style={{ cursor: 'pointer' }}>page{ind('pageKey')}</th>
                  <th onClick={() => toggleSort('username')} style={{ cursor: 'pointer' }}>model{ind('username')}</th>
                  <th className="num" onClick={() => toggleSort('views')} style={{ cursor: 'pointer' }}>views{ind('views')}</th>
                  <th className="num" onClick={() => toggleSort('clicks')} style={{ cursor: 'pointer' }}>clicks{ind('clicks')}</th>
                  <th className="num" onClick={() => toggleSort('ctr')} style={{ cursor: 'pointer' }}>CTR{ind('ctr')}</th>
                  <th>type</th>
                  <th className="num" onClick={() => toggleSort('cpc')} style={{ cursor: 'pointer' }}>CPC{ind('cpc')}</th>
                  <th className="num" onClick={() => toggleSort('revenue')} style={{ cursor: 'pointer' }}>revenue{ind('revenue')}</th>
                  <th className="num" onClick={() => toggleSort('rpm')} style={{ cursor: 'pointer' }}>RPM{ind('rpm')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSorted.slice(0, DISPLAY_LIMIT).map(r => (
                  <tr key={rowKey(r.pageKey, r.username)}>
                    <td><span className="hf-mono">{r.pageKey}</span></td>
                    <td><span className="hf-mono">{r.username}</span></td>
                    <td className="num" style={{ fontWeight: 600 }}>{fmtNumber(r.views)}</td>
                    <td className="num">{fmtNumber(r.clicks)}</td>
                    <td className="num hf-mono">{pct(r.ctr)}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: monColor(r.monetizationType), flex: '0 0 8px' }} />
                        {r.monetizationType}
                      </span>
                    </td>
                    <td className="num hf-mono">{r.monetizationType === 'PPC' ? fmtCurrency(r.cpc) : '—'}</td>
                    <td className="num hf-mono">{r.revenue > 0 ? fmtCurrency(r.revenue) : '—'}</td>
                    <td className="num hf-mono">{r.rpm > 0 ? fmtCurrency(r.rpm) : '—'}</td>
                  </tr>
                ))}
                {filteredSorted.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', color: 'var(--fg-3)', padding: '24px 0' }}>
                      No data for this range.
                    </td>
                  </tr>
                )}
                {filteredSorted.length > DISPLAY_LIMIT && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', color: 'var(--fg-3)', padding: '12px 0', fontSize: 12 }}>
                      Showing top {fmtNumber(DISPLAY_LIMIT)} of {fmtNumber(filteredSorted.length)} rows · KPIs &amp; chart cover all of them · narrow with filters or Export CSV for the full list
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AnalyticsDashboardPage;
